// @file Surface language panel store
import { trackEvent } from '@@/bits/analytics'
import { setDirAttribute } from '@@/bits/current_dir'
import { getHyphenatedCurrentLocale, setLangAttribute } from '@@/bits/current_locale'
import { captureFetchException } from '@@/bits/error_tracker'
import { addTranslations, allTranslations, p__, setLocale, __ } from '@@/bits/intl'
import { loadJs } from '@@/bits/load'
import { availableLocales, getLocaleFileName, getLocaleName, isRealLocale, isRtlLocale } from '@@/bits/locale'
import { clearSearchParam, getSearchParam, setSearchParam } from '@@/bits/location'
import { getVuexStore } from '@@/bits/pinia'
import { SnackbarNotificationType, useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useSurfaceStore } from '@@/pinia/surface'
import { useRealtimeStore } from '@@/pinia/surface_realtime'
import { WallLanguageDetectionApi } from '@@/surface/api/wall_language_detection'
import { WallTranslationApi } from '@@/surface/api/wall_translation'
import type { TranslatedWall } from '@@/types'
import type { RootState } from '@@/vuexstore/surface/types'
import { uniqueId } from 'es-toolkit/compat'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export const ORIGINAL_LANGUAGE_KEY = 'original'

export const useSurfaceTranslationsStore = defineStore('surfaceTranslationsStore', () => {
  const surfaceVuexStore = getVuexStore<RootState>()
  const surfaceStore = useSurfaceStore()
  const globalSnackbarStore = useGlobalSnackbarStore()
  const surfaceRealtimeStore = useRealtimeStore()

  const xSurfaceLanguagePanel = ref<boolean>(false)
  const currentLanguage = ref<string>(ORIGINAL_LANGUAGE_KEY) // Holds the language code that is used to translate the padlet
  const unsavedLanguage = ref<string>(currentLanguage.value) // Temporary storage for the language code that the user has selected to translate the padlet before saving it
  const detectedLanguage = ref<string>('') // Holds the language code that is detected from the padlet
  const isTranslatingPadletFromPanel = ref<boolean>(false)
  const isTranslatingPadletFromBanner = ref<boolean>(false)
  const isDetectingLanguage = ref<boolean>(false)

  const isViewingTranslatedPadlet = computed<boolean>(() => currentLanguage.value !== ORIGINAL_LANGUAGE_KEY)
  const isLangParamPresent = computed<boolean>(() => getSearchParam('lang') != null)

  const userLocale = getHyphenatedCurrentLocale()
  const userLocaleDisplayName = computed<string>(() => {
    return getLocaleName(userLocale)
  })

  // Presentable name for current language with custom logic for original language
  const currentLanguageDisplayName = computed<string>(() => {
    if (currentLanguage.value === ORIGINAL_LANGUAGE_KEY) {
      return p__(
        'This text appears in the language selector panel. It shows the original language of the content. localeName is the name of the language that we have detected the content to be in.',
        'Original ({localeName})',
        { localeName: detectedLanguageDisplayName.value },
      )
    }
    return availableLocales.find((locale) => locale.html === currentLanguage.value)?.name ?? ''
  })

  const detectedLanguageDisplayName = computed<string>(() => {
    const displayName = availableLocales.find((locale) => locale.html === detectedLanguage.value)?.name
    if (detectedLanguage.value === 'multiple') {
      return p__('Indicates that the text is in multiple languages', 'multiple languages')
    } else if (detectedLanguage.value === 'unknown' || displayName == null) {
      return p__('Indicates that the text is in a language that we could not detect', 'Unknown')
    } else if (detectedLanguage.value === 'en') {
      return __('English - US')
    }
    return displayName
  })

  const showLanguagePanel = (): void => {
    xSurfaceLanguagePanel.value = true
  }

  const closeLanguagePanel = (): void => {
    xSurfaceLanguagePanel.value = false
    // Reset unsaved language to current language
    unsavedLanguage.value = currentLanguage.value
  }

  const handleRealtimeConnection = (selectedLanguage: string): void => {
    if (selectedLanguage !== ORIGINAL_LANGUAGE_KEY) {
      void surfaceVuexStore?.dispatch('fallbackToRestApi', null, { root: true })
      surfaceRealtimeStore.disconnectBrahms()
    } else {
      void surfaceVuexStore?.dispatch('revertToWs', null, { root: true })
      surfaceRealtimeStore.createNewBrahmsConnectionForUser()
    }
  }

  const translatedWall = ref<TranslatedWall | null>(null)

  // Randomly generated key to force re-render of the surface app when the language changes
  const surfaceAppKey = ref<string>(uniqueId())
  const regenerateSurfaceAppKey = (): void => {
    surfaceAppKey.value = uniqueId()
  }

  // Helper method to update the current locale, lang and dir attribute of the document
  const updateDocumentLanguageAttributes = (locale: string): void => {
    setLocale(locale)
    setLangAttribute(locale)
    setDirAttribute(isRtlLocale(locale) ? 'rtl' : 'ltr')
    regenerateSurfaceAppKey()
  }

  const loadTranslationsIfNeeded = async (locale: string): Promise<boolean> => {
    // Check if translations are already loaded
    if (allTranslations[locale] != null) return true

    try {
      const translationScriptUrl = await WallTranslationApi.fetchIntlTranslationScriptUrl(locale)
      if (translationScriptUrl == null) return false

      await loadJs(translationScriptUrl)
      addTranslations(window.$intl)
      return true
    } catch (error) {
      captureFetchException(error, { source: 'SurfaceLoadIntlTranslations' })
      return false
    }
  }

  const handleInterfaceUpdates = async (locale: string): Promise<void> => {
    // No need to load translations for the following scenarios:
    // 1. Changing to original language or user's account language, as intl translations are already loaded
    // Case of original language and user's account language have the same behavior because they should both display the UI
    // in the user's account language.
    // 2. Changing to English, as no intl translations are needed.
    if (locale === ORIGINAL_LANGUAGE_KEY || locale === userLocale || locale === 'en') {
      const updatedLocale = locale === ORIGINAL_LANGUAGE_KEY ? userLocale : locale
      updateDocumentLanguageAttributes(updatedLocale)
      return
    }

    // Load and apply translations using the file name for the locale
    const localeFileName = getLocaleFileName(locale)
    if (localeFileName == null) return

    const translationsLoaded = await loadTranslationsIfNeeded(localeFileName)
    if (translationsLoaded) {
      updateDocumentLanguageAttributes(locale)
    }
  }

  async function handleTranslation(selectedLanguage: string, source: 'panel' | 'banner'): Promise<void> {
    try {
      if (selectedLanguage !== ORIGINAL_LANGUAGE_KEY) {
        setSearchParam('lang', selectedLanguage)
        // Ensure that the banner is displayed, especially in the event where the user has dismissed it and then translated the padlet
        displayTranslationBanner()
      } else {
        clearSearchParam('lang')
      }
      handleRealtimeConnection(selectedLanguage)

      if (source === 'panel') {
        isTranslatingPadletFromPanel.value = true
      } else {
        isTranslatingPadletFromBanner.value = true
      }

      translatedWall.value = await WallTranslationApi.translate(surfaceStore.wallId, selectedLanguage)
      await handleInterfaceUpdates(selectedLanguage)
      currentLanguage.value = selectedLanguage
    } catch (e) {
      const translationFailedSnackbarUid = globalSnackbarStore.setSnackbar({
        message: __('Padlet could not be translated'),
        notificationType: SnackbarNotificationType.error,
        timeout: 5000,
        actionText: __('Retry'),
        actionTextActions: [
          async () => {
            globalSnackbarStore.removeSnackbar(translationFailedSnackbarUid)
            await handleTranslation(selectedLanguage, source)
          },
        ],
      })
      clearSearchParam('lang')
      dismissTranslationBanner()
      captureFetchException(e, { source: 'SurfaceTranslatePadletToLanguage' })
    } finally {
      isTranslatingPadletFromPanel.value = false
      isTranslatingPadletFromBanner.value = false
      closeLanguagePanel()
    }
  }

  async function translatePadletFromLanguagePanel(selectedLanguage: string): Promise<void> {
    if (isTranslatingPadletFromBanner.value || isTranslatingPadletFromPanel.value) return
    trackEvent('Surface', 'Translated padlet from language panel', null, null, { language: selectedLanguage })
    await handleTranslation(selectedLanguage, 'panel')
  }

  async function translatePadletFromBanner(selectedLanguage: string): Promise<void> {
    if (isTranslatingPadletFromBanner.value || isTranslatingPadletFromPanel.value) return
    trackEvent('Surface', 'Translated padlet from language banner', null, null, { language: selectedLanguage })
    await handleTranslation(selectedLanguage, 'banner')
  }

  async function detectLanguage(): Promise<void> {
    try {
      isDetectingLanguage.value = true
      const data = await WallLanguageDetectionApi.detectLanguage(surfaceStore.wallId)
      detectedLanguage.value = data.language
    } catch (e) {
      captureFetchException(e, { source: 'SurfaceLanguageDetection' })
    } finally {
      isDetectingLanguage.value = false
    }
  }

  const hasUnsavedLanguageChanges = computed<boolean>(() => unsavedLanguage.value !== currentLanguage.value)

  const setUnsavedLanguage = (language: string): void => {
    unsavedLanguage.value = language
  }

  const hasDismissedTranslationBanner = ref<boolean>(false)
  const dismissTranslationBanner = (): void => {
    hasDismissedTranslationBanner.value = true
  }
  const displayTranslationBanner = (): void => {
    hasDismissedTranslationBanner.value = false
  }

  const xRevertToOriginalBanner = computed<boolean>(() => isViewingTranslatedPadlet.value)
  const xTranslateToUserLocaleBanner = computed<boolean>(
    () =>
      !isViewingTranslatedPadlet.value &&
      detectedLanguage.value !== userLocale &&
      isRealLocale(userLocale) &&
      !surfaceVuexStore?.getters.amIOwner,
  )

  return {
    isDetectingLanguage,
    xSurfaceLanguagePanel,
    currentLanguage,
    detectedLanguage,
    unsavedLanguage,
    isTranslatingPadletFromPanel,
    isTranslatingPadletFromBanner,
    showLanguagePanel,
    closeLanguagePanel,
    translatePadletFromLanguagePanel,
    translatePadletFromBanner,
    detectLanguage,
    hasUnsavedLanguageChanges,
    setUnsavedLanguage,
    currentLanguageDisplayName,
    isViewingTranslatedPadlet,
    isLangParamPresent,
    hasDismissedTranslationBanner,
    dismissTranslationBanner,
    xRevertToOriginalBanner,
    xTranslateToUserLocaleBanner,
    userLocaleDisplayName,
    surfaceAppKey,
    translatedWall,
    userLocale,
    detectedLanguageDisplayName,
  }
})
